import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';
import { worksData, newsData } from '../../mocks/works';

export default function HomePage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const featuredWorks = worksData.slice(0, 3);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'New Age Cinema',
      url: siteUrl,
      description: '映像作家 EIJI HOSHINOの公式ポートフォリオサイト。自主制作映画・MV・アニメーション・実験映像など多彩な映像作品を発信。',
      inLanguage: 'ja',
      author: {
        '@type': 'Person',
        name: 'EIJI HOSHINO',
        url: `${siteUrl}/about`,
        jobTitle: '映像作家 / Filmmaker',
        sameAs: [
          'https://www.youtube.com/channel/UC8JD2H3pNUagGOp6GsW_kKQ',
          'https://x.com/EIJIHOSHINO_NAC',
          'https://www.instagram.com/eiji_hoshino',
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Person',
      name: 'EIJI HOSHINO',
      url: `${siteUrl}/about`,
      jobTitle: '映像作家 / Filmmaker',
      description: '自主制作映画・MV・実験映像を手がける映像作家。脚本・撮影・編集まで一貫して制作。',
      sameAs: [
        'https://www.youtube.com/channel/UC8JD2H3pNUagGOp6GsW_kKQ',
        'https://x.com/EIJIHOSHINO_NAC',
        'https://www.instagram.com/eiji_hoshino',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="New Age Cinema | 映像作家 EIJI HOSHINO ポートフォリオ"
        description="映像作家 EIJI HOSHINOの公式ポートフォリオサイト。自主制作映画・MV・アニメーション・実験映像など多彩な映像作品を発信。脚本・撮影・編集まで一貫して手がける映像制作の全てをご覧ください。"
        keywords="映像作家,映画制作,MV制作,実験映像,EIJI HOSHINO,New Age Cinema,ポートフォリオ"
        canonical="/"
        jsonLd={jsonLd}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-[70vh] md:h-[85vh] flex items-center justify-center pt-16 md:pt-20">
        <div className={`text-center transition-all duration-1000 px-5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16 mb-8">
            <h1 className="text-4xl md:text-7xl font-light tracking-[0.2em] md:tracking-[0.3em] text-black md:writing-vertical" style={{ writingMode: window.innerWidth >= 768 ? 'vertical-rl' : 'horizontal-tb' }}>
              考えるな、つくれ
            </h1>
            <p className="text-sm md:text-lg font-light tracking-[0.3em] md:tracking-[0.4em] text-gray-600">
              Don't think. Create.
            </p>
          </div>
          <div className="flex justify-center">
            <div className="w-2 h-2 bg-[#00F0FF] animate-pulse"></div>
          </div>
        </div>

        <div className="absolute bottom-8 md:bottom-12 left-1/2 -translate-x-1/2">
          <div className="w-px h-10 md:h-16 bg-[#00F0FF] animate-bounce"></div>
        </div>
      </section>

      {/* News Section */}
      <section className="px-5 md:px-12 py-16 md:py-32">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xs font-light tracking-[0.2em] text-gray-400 mb-8 md:hidden">NEWS</h2>
          <div className="flex gap-8 md:gap-24">
            <div className="hidden md:block w-1/4">
              <h2 className="text-xs font-light tracking-[0.2em] text-gray-400" style={{ writingMode: 'vertical-rl' }}>
                NEWS
              </h2>
            </div>
            <div className="flex-1 space-y-6 md:space-y-8">
              {newsData.map((news, index) => (
                <div
                  key={index}
                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-8 pb-5 md:pb-6 border-b border-gray-200 group cursor-pointer transition-all duration-300 hover:translate-x-2"
                >
                  <div className="hidden md:block w-0 h-12 bg-[#00F0FF] transition-all duration-300 group-hover:w-1"></div>
                  <span className="text-xs md:text-sm font-mono text-[#00F0FF] whitespace-nowrap">{news.date}</span>
                  <p className="text-sm md:text-base font-light text-black">{news.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Works Preview Section */}
      <section className="px-5 md:px-12 py-16 md:py-32 relative">
        <div className="hidden md:block absolute left-12 top-1/2 -translate-y-1/2">
          <h2 className="text-sm font-light tracking-[0.2em] text-gray-400" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
            SELECTED WORKS
          </h2>
        </div>
        <h2 className="text-xs font-light tracking-[0.2em] text-gray-400 mb-8 md:hidden">SELECTED WORKS</h2>

        <div className="max-w-7xl mx-auto md:pl-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-16">
            {featuredWorks.map((work, index) => (
              <Link
                key={work.id}
                to={`/works/${work.id}`}
                className="group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative overflow-hidden mb-4 md:mb-6 aspect-video">
                  <img
                    src={work.thumbnail}
                    alt={work.title}
                    className="w-full h-full object-cover object-top grayscale group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#00F0FF] transition-all duration-300"></div>
                </div>
                <div className="flex items-center gap-3 mb-1 md:mb-2">
                  <h3 className="text-sm font-light text-black">{work.title}</h3>
                  <div className="w-1 h-1 bg-[#00F0FF] rounded-full"></div>
                </div>
                <p className="text-xs font-mono text-gray-500">{work.year}</p>
              </Link>
            ))}
          </div>

          <div className="flex justify-end mt-10 md:mt-16">
            <Link
              to="/works"
              className="text-sm font-light tracking-[0.2em] text-black hover:text-[#00F0FF] transition-colors duration-300 group flex items-center gap-2"
            >
              VIEW ALL WORKS
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-2">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* External Links Section */}
      <section className="px-5 md:px-12 py-16 md:py-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <a
            href="https://windies-mania.net/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="border border-gray-200 p-10 md:p-16 flex flex-col items-center justify-center gap-5 md:gap-8 hover:bg-[#00F0FF] hover:border-[#00F0FF] transition-all duration-400 group"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center overflow-hidden">
              <img
                src="https://static.readdy.ai/image/f493cbe5c2084134677121347fdba237/214be233785e39ebb6795040f74f7c21.png"
                alt="ウィンディーズマニア！シャバダバ！ Logo"
                className="w-full h-full object-contain group-hover:brightness-0 group-hover:invert transition-all duration-400"
              />
            </div>
            <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black group-hover:text-white transition-colors duration-400 text-center">
              ウィンディーズマニア！<br />シャバダバ！
            </h3>
            <span className="text-xs font-light tracking-[0.2em] text-black group-hover:text-white border border-black group-hover:border-white group-hover:bg-white group-hover:text-[#00F0FF] px-6 md:px-8 py-3 transition-all duration-400 whitespace-nowrap">
              VISIT SITE
            </span>
          </a>

          <a
            href="https://the-zero-movement.com/"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="border border-gray-200 p-10 md:p-16 flex flex-col items-center justify-center gap-5 md:gap-8 hover:bg-[#00F0FF] hover:border-[#00F0FF] transition-all duration-400 group"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center overflow-hidden">
              <img
                src="https://static.readdy.ai/image/f493cbe5c2084134677121347fdba237/740378349ec95697002f553d45d72669.png"
                alt="劇団第０楽章 Logo"
                className="w-full h-full object-contain group-hover:brightness-0 group-hover:invert transition-all duration-400"
              />
            </div>
            <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black group-hover:text-white transition-colors duration-400">
              劇団第０楽章
            </h3>
            <span className="text-xs font-light tracking-[0.2em] text-black group-hover:text-white border border-black group-hover:border-white group-hover:bg-white group-hover:text-[#00F0FF] px-6 md:px-8 py-3 transition-all duration-400 whitespace-nowrap">
              VISIT SITE
            </span>
          </a>

          <a
            href="https://www.kerokero-cham.net/kerohoshi"
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="border border-gray-200 p-10 md:p-16 flex flex-col items-center justify-center gap-5 md:gap-8 hover:bg-[#00F0FF] hover:border-[#00F0FF] transition-all duration-400 group"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center">
              <img src="https://public.readdy.ai/ai/img_res/4257f1ec-686b-4976-9a8a-63cf1b5ca8e3.png" alt="ケロボシ ロゴ" className="w-full h-full object-contain" />
            </div>
            <h3 className="text-base md:text-lg font-light tracking-[0.1em] text-black group-hover:text-white transition-colors duration-400">
              ケロボシ
            </h3>
            <span className="text-xs font-light tracking-[0.2em] text-black group-hover:text-white border border-black group-hover:border-white group-hover:bg-white group-hover:text-[#00F0FF] px-6 md:px-8 py-3 transition-all duration-400 whitespace-nowrap">
              VISIT SITE
            </span>
          </a>
        </div>
      </section>

      {/* SNS Section */}
      <section className="px-5 md:px-12 py-16 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl md:text-2xl font-light tracking-[0.2em] text-center mb-10 md:mb-16">SOCIAL MEDIA</h2>
          <div className="flex items-center justify-center gap-12 md:gap-20">
            <a
              href="https://www.youtube.com/channel/UC8JD2H3pNUagGOp6GsW_kKQ"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-4 group cursor-pointer"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-gray-300 flex items-center justify-center group-hover:border-[#00F0FF] group-hover:bg-[#00F0FF] transition-all duration-300">
                <i className="ri-youtube-fill text-3xl md:text-4xl text-gray-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <span className="text-xs font-light tracking-[0.2em] text-gray-600 group-hover:text-[#00F0FF] transition-colors duration-300">YOUTUBE</span>
            </a>

            <a
              href="https://x.com/EIJIHOSHINO_NAC"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-4 group cursor-pointer"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-gray-300 flex items-center justify-center group-hover:border-[#00F0FF] group-hover:bg-[#00F0FF] transition-all duration-300">
                <i className="ri-twitter-x-fill text-3xl md:text-4xl text-gray-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <span className="text-xs font-light tracking-[0.2em] text-gray-600 group-hover:text-[#00F0FF] transition-colors duration-300">X</span>
            </a>

            <a
              href="https://www.instagram.com/eiji_hoshino?igsh=d2p4MWhlaXg2dXdu&utm_source=qr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-4 group cursor-pointer"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border-2 border-gray-300 flex items-center justify-center group-hover:border-[#00F0FF] group-hover:bg-[#00F0FF] transition-all duration-300">
                <i className="ri-instagram-fill text-3xl md:text-4xl text-gray-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <span className="text-xs font-light tracking-[0.2em] text-gray-600 group-hover:text-[#00F0FF] transition-colors duration-300">INSTAGRAM</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
